import axios from 'axios'
import { Buffer } from 'buffer'
import {
  getCanvasPhotoPreviewUrl,
  isPhotoPreviewUrl,
} from 'components/PhotoBank/PhotoBank.utils'

const imagesCache: Record<string, WeakRef<HTMLImageElement>> = {}

const addImageToCache = (url: string, image: HTMLImageElement) => {
  imagesCache[url] = new WeakRef(image)
}

export const removeImageFromCache = (url: string) => {
  delete imagesCache[url]
}

const getImageFromCache = (url: string) => {
  return imagesCache[url]?.deref()
}

export const isPresignedS3Url = (url: string) =>
  url.includes('X-Amz-Signature=')

// Enough for the pages around the open spread of a photo book, at a few hundred KB per preview
const CORS_IMAGE_BLOBS_CACHE_SIZE = 60
const corsImageBlobsCache = new Map<string, Promise<Blob>>()

const fetchCorsImageBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url, { mode: 'cors', cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Failed to fetch image status=${response.status}`)
  }
  return response.blob()
}

const getCorsImageBlob = (url: string): Promise<Blob> => {
  const cachedBlobPromise = corsImageBlobsCache.get(url)
  if (cachedBlobPromise) {
    corsImageBlobsCache.delete(url)
    corsImageBlobsCache.set(url, cachedBlobPromise)
    return cachedBlobPromise
  }

  const blobPromise = fetchCorsImageBlob(url)
  corsImageBlobsCache.set(url, blobPromise)
  blobPromise.catch(() => corsImageBlobsCache.delete(url))
  if (corsImageBlobsCache.size > CORS_IMAGE_BLOBS_CACHE_SIZE) {
    const [leastRecentUrl] = corsImageBlobsCache.keys()
    corsImageBlobsCache.delete(leastRecentUrl)
  }
  return blobPromise
}

// S3 sends CORS and Vary headers only when the request has an Origin header, so a presigned image first shown
// by a plain <img> sits in the HTTP cache without them and a later crossOrigin load of it fails. The first load
// bypasses the HTTP cache and later loads reuse the downloaded copy, so turning pages back doesn't download again.
// The caller revokes the returned blob URL once the image is loaded.
export const getCorsImageBlobUrl = async (url: string): Promise<string> =>
  URL.createObjectURL(await getCorsImageBlob(url))

const loadCorsImageFromBlob = async (
  url: string
): Promise<HTMLImageElement> => {
  const blobUrl = await getCorsImageBlobUrl(url)
  try {
    return await loadImage(blobUrl, true)
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

export const loadImage = (
  url: string,
  crossOrigin: boolean = false,
  cache: boolean = false
): Promise<HTMLImageElement> => {
  const imageFromCache = getImageFromCache(url)
  if (cache && imageFromCache) {
    return new Promise((resolve) => resolve(imageFromCache))
  }
  if (crossOrigin && isPhotoPreviewUrl(url)) {
    return loadImageElement(
      getCanvasPhotoPreviewUrl(url),
      crossOrigin,
      false
    ).then((image) => {
      if (cache) {
        addImageToCache(url, image)
      }
      return image
    })
  }
  if (crossOrigin && isPresignedS3Url(url)) {
    return loadCorsImageFromBlob(url).then((image) => {
      if (cache) {
        addImageToCache(url, image)
      }
      return image
    })
  }

  return loadImageElement(url, crossOrigin, cache)
}

const loadImageElement = (
  url: string,
  crossOrigin: boolean,
  cache: boolean
): Promise<HTMLImageElement> => {
  const image: HTMLImageElement = new window.Image()
  const extension: string = url.split('.')?.pop() || 'png'
  image.style.maxWidth = '100%'
  if (crossOrigin) {
    image.crossOrigin = 'anonymous'
  }

  return new Promise((resolve, reject) => {
    image.onload = () => {
      if (cache) {
        addImageToCache(url, image)
      }
      resolve(image)
    }

    image.onerror = () => {
      setTimeout(() => {
        axios
          .get(url, {
            responseType: 'arraybuffer',
          })
          .then((response) => {
            const uri: string = Buffer.from(response.data, 'binary').toString(
              'base64'
            )
            image.onload = () => {
              if (cache) {
                addImageToCache(url, image)
              }
              resolve(image)
            }
            image.onerror = (error) => {
              reject(
                new Error(
                  `Failed to load image with dataUri - ${error.toString()}`
                )
              )
            }
            image.src = `data:image/${extension};base64,${uri}`
          })
          .catch((err: Error) => {
            const errorMessage = err?.message || err
            reject(
              new Error(`Failed to fetch image err=${errorMessage}`, {
                cause: err,
              })
            )
          })
      }, 2000)
    }

    image.src = url
  })
}

export function imageToBlob(image: HTMLImageElement): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = image.width
    canvas.height = image.height
    ctx?.drawImage(image, 0, 0)
    canvas.toBlob((blob) => {
      if (blob) {
        const blobUrl = URL.createObjectURL(blob)
        resolve(blobUrl)
      }
    })
  })
}
